import { Locale } from '@/i18n-config';

export type Localized<T> = Record<Locale, T>;

export type MvpFeature = {
  title: Localized<string>;
  description: Localized<string>;
};

export type MvpMetric = {
  label: Localized<string>;
  value: Localized<string>;
};

export type MvpStep = {
  title: Localized<string>;
  description: Localized<string>;
  duration?: Localized<string>;
};

export type MvpPageEntry = {
  slug: string;
  sectorSlug: string;
  image: string;
  imageAlt: Localized<string>;
  northStarQuery: {
    sector: string;
    prompt: Localized<string>;
  };
  seo: {
    title: Localized<string>;
    description: Localized<string>;
    keywords: Localized<string[]>;
  };
  hero: {
    eyebrow: Localized<string>;
    title: Localized<string>;
    highlightedTitle: Localized<string>;
    description: Localized<string>;
    primaryCta: Localized<string>;
    secondaryCta: Localized<string>;
  };
  problem: {
    eyebrow: Localized<string>;
    title: Localized<string>;
    subtitle: Localized<string>;
    pains: { title: Localized<string>; description: Localized<string> }[];
  };
  concept: {
    eyebrow: Localized<string>;
    title: Localized<string>;
    description: Localized<string>;
    features: MvpFeature[];
  };
  impact: {
    eyebrow: Localized<string>;
    title: Localized<string>;
    subtitle: Localized<string>;
    metrics: MvpMetric[];
  };
  delivery: {
    eyebrow: Localized<string>;
    title: Localized<string>;
    subtitle: Localized<string>;
    steps: MvpStep[];
  };
  cta: {
    title: Localized<string>;
    highlightedTitle: Localized<string>;
    description: Localized<string>;
    primaryCta: Localized<string>;
    secondaryCta: Localized<string>;
  };
};

const i18n = (fr: string, en: string): Localized<string> => ({ fr, en });
const i18nArr = (fr: string[], en: string[]): Localized<string[]> => ({ fr, en });

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export const mvpPageEntries: MvpPageEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // ÉDUCATION & EDTECH
  // ─────────────────────────────────────────────────────────────
  {
    slug: 'lms-avec-parcours-adaptatif',
    sectorSlug: 'education',
    image: 'https://ik.imagekit.io/lindahestia/articles/05-lms-parcours-adaptatif.webp?tr=f-webp',
    imageAlt: i18n('Interface LMS avec parcours adaptatif', 'Adaptive LMS interface'),
    northStarQuery: {
      sector: 'education',
      prompt: i18n(
        "Les apprenants abandonnent parce que le parcours n'est pas adapté à leur niveau",
        'Learners drop out because the path is not adapted to their level'
      ),
    },
    seo: {
      title: i18n('MVP LMS avec parcours adaptatif | Averiane', 'Adaptive LMS MVP | Averiane'),
      description: i18n(
        'Construisez un MVP LMS qui repère les blocages pédagogiques et propose un parcours personnalisé à chaque apprenant.',
        'Build an LMS MVP that spots learning blockers and proposes a personalized path for each learner.'
      ),
      keywords: i18nArr(
        ['LMS', 'parcours adaptatif', 'EdTech', 'MVP éducation', 'personnalisation pédagogique'],
        ['LMS', 'adaptive learning path', 'EdTech', 'education MVP', 'personalized learning']
      ),
    },
    hero: {
      eyebrow: i18n('Éducation & EdTech', 'Education & EdTech'),
      title: i18n('LMS avec', 'Adaptive'),
      highlightedTitle: i18n('parcours adaptatif', 'learning path'),
      description: i18n(
        'Un MVP qui diagnosticque les blocages de chaque apprenant et ajuste automatiquement le contenu pour réduire l’abandon et accélérer la montée en compétence.',
        'An MVP that diagnoses each learner’s blockers and automatically adjusts content to reduce dropout and speed up skill acquisition.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n('Pourquoi les apprenants abandonnent ?', 'Why do learners drop out?'),
      subtitle: i18n(
        'Parcours linéaires, contenus inadaptés, manque de visibilité sur la progression : votre LMS actuel coûte cher en abandons et en réengagement.',
        'Linear paths, unsuitable content, no visibility on progress: your current LMS is expensive in dropouts and re-engagement.'
      ),
      pains: [
        {
          title: i18n('Parcours identiques pour tous', 'One-size-fits-all paths'),
          description: i18n(
            'Les apprenants avancés s’ennuient, les plus fragiles se découragent. Le taux d’abandon grimpe dès la moitié de la formation.',
            'Advanced learners get bored; weaker ones get discouraged. Dropout rises by mid-training.'
          ),
        },
        {
          title: i18n('Pas de feedback en temps réel', 'No real-time feedback'),
          description: i18n(
            'Sans indicateur de compréhension, les formateurs découvrent les échecs trop tard, lors des examens ou des enquêtes de satisfaction.',
            'Without comprehension indicators, trainers discover failures too late, at exams or satisfaction surveys.'
          ),
        },
        {
          title: i18n('Données dispersées', 'Scattered data'),
          description: i18n(
            'CRM, LMS et outils de visio stockent chacun des informations critiques sans jamais les croiser pour guider la pédagogie.',
            'CRM, LMS, and video tools each store critical information without ever cross-referencing it to guide pedagogy.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un MVP qui apprend de vos apprenants', 'An MVP that learns from your learners'),
      description: i18n(
        'Un module léger intégré à votre LMS existant. Il collecte les interactions, identifie les points de fragilité et propose automatiquement des ressources adaptées.',
        'A lightweight module integrated into your existing LMS. It collects interactions, identifies weak points, and automatically suggests suitable resources.'
      ),
      features: [
        {
          title: i18n('Diagnostic pédagogique', 'Learning diagnostic'),
          description: i18n(
            'Score de maîtrise par compétence, temps passé, tentatives échouées : un portrait précis de chaque apprenant.',
            'Mastery score per skill, time spent, failed attempts: a precise profile of each learner.'
          ),
        },
        {
          title: i18n('Recommandations de contenu', 'Content recommendations'),
          description: i18n(
            'Vidéos, quizzes ou fiches proposées en fonction du niveau réel, pas du programme prévu.',
            'Videos, quizzes, or sheets proposed based on actual level, not the planned curriculum.'
          ),
        },
        {
          title: i18n('Alertes formateurs', 'Trainer alerts'),
          description: i18n(
            'Tableau de bord simple qui signale les apprenants en difficulté avant qu’ils ne basculent.',
            'A simple dashboard that flags struggling learners before they drop out.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 8 semaines', 'What you gain in 8 weeks'),
      subtitle: i18n(
        'Des premiers résultats dès la phase pilote, mesurés sur un échantillon de parcours réels.',
        'First results from the pilot phase, measured on a sample of real learner paths.'
      ),
      metrics: [
        { label: i18n('Taux d’abandon', 'Dropout rate'), value: i18n('-25 %', '-25%') },
        { label: i18n('Temps de complétion', 'Completion time'), value: i18n('-20 %', '-20%') },
        {
          label: i18n('Satisfaction apprenants', 'Learner satisfaction'),
          value: i18n('+30 %', '+30%'),
        },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n('Du diagnostic au transfert', 'From diagnosis to handover'),
      subtitle: i18n(
        'Une approche en 3 temps pour valider rapidement la valeur sans perturber votre LMS existant.',
        'A 3-step approach to quickly validate value without disrupting your existing LMS.'
      ),
      steps: [
        {
          title: i18n('Audit des données pédagogiques', 'Audit learning data'),
          description: i18n(
            'Nous identifions les points de données disponibles et les premiers signaux d’abandon.',
            'We identify available data points and early dropout signals.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('MVP de personnalisation', 'Personalization MVP'),
          description: i18n(
            'Un algorithme léger et un dashboard formateur testés sur un sous-ensemble de parcours.',
            'A lightweight algorithm and trainer dashboard tested on a subset of learning paths.'
          ),
          duration: i18n('Semaines 2-5', 'Weeks 2-5'),
        },
        {
          title: i18n('Intégration et transfert', 'Integration and handover'),
          description: i18n(
            'Connexion à votre LMS, documentation et transfert à votre équipe ou hébergement clé en main.',
            'Connection to your LMS, documentation, and handover to your team or turnkey hosting.'
          ),
          duration: i18n('Semaines 6-8', 'Weeks 6-8'),
        },
      ],
    },
    cta: {
      title: i18n('Prêt à réduire l’abandon', 'Ready to reduce dropout'),
      highlightedTitle: i18n('avec un parcours adaptatif ?', 'with an adaptive path?'),
      description: i18n(
        'Discutons de vos données et de votre LMS. Nous vous proposons un MVP mesurable en 20 minutes.',
        'Let’s discuss your data and LMS. We’ll propose a measurable MVP in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },
  {
    slug: 'automation-des-certifications',
    sectorSlug: 'education',
    image:
      'https://ik.imagekit.io/lindahestia/articles/06-automation-certifications.webp?tr=f-webp',
    imageAlt: i18n(
      'Attestations de certification générées automatiquement',
      'Automatically generated certification certificates'
    ),
    northStarQuery: {
      sector: 'education',
      prompt: i18n(
        'La délivrance des attestations de formation prend trop de temps et crée des erreurs',
        'Certificate delivery takes too long and creates errors'
      ),
    },
    seo: {
      title: i18n(
        'MVP Automation des certifications | Averiane',
        'Certification automation MVP | Averiane'
      ),
      description: i18n(
        'Automatisez la création, la vérification et l’envoi des attestations de formation en un MVP clé en main.',
        'Automate the creation, verification, and delivery of training certificates in a turnkey MVP.'
      ),
      keywords: i18nArr(
        ['attestation', 'certification', 'automation', 'MVP éducation', 'diplôme numérique'],
        ['certificate', 'certification', 'automation', 'education MVP', 'digital credential']
      ),
    },
    hero: {
      eyebrow: i18n('Éducation & EdTech', 'Education & EdTech'),
      title: i18n('Automation des', 'Certification'),
      highlightedTitle: i18n('certifications', 'automation'),
      description: i18n(
        'Générez, signez, vérifiez et envoyez les attestations de formation sans intervention manuelle, en respectant vos exigences Qualiopi.',
        'Generate, sign, verify, and send training certificates with no manual work, while meeting your Qualiopi requirements.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n('La certification, un goulot d’étranglement', 'Certification: a bottleneck'),
      subtitle: i18n(
        'Temps de traitement, erreurs de saisie, attestations perdues : chaque session validée génère des heures de travail administratif.',
        'Processing time, input errors, lost certificates: every validated session generates hours of admin work.'
      ),
      pains: [
        {
          title: i18n('Génération manuelle', 'Manual generation'),
          description: i18n(
            'Word, PDF, signatures scannées : la moindre coquille oblige à refaire l’attestation et à la renvoyer.',
            'Word, PDF, scanned signatures: any typo forces you to redo and resend the certificate.'
          ),
        },
        {
          title: i18n('Vérification fastidieuse', 'Tedious verification'),
          description: i18n(
            'Employeurs et financeurs demandent une preuve fiable, mais vous n’avez pas de portail public de vérification.',
            'Employers and funders ask for reliable proof, but you have no public verification portal.'
          ),
        },
        {
          title: i18n('Traçabilité imparfaite', 'Imperfect traceability'),
          description: i18n(
            'Quand un audit Qualiopi frappe, rassembler les preuves prend des jours.',
            'When a Qualiopi audit hits, gathering proof takes days.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un générateur d’attestations connecté', 'A connected certificate generator'),
      description: i18n(
        'Le MVP récupère les données de présence et de résultats, génère un PDF signé et délivre un lien de vérification sécurisé.',
        'The MVP pulls attendance and results data, generates a signed PDF, and delivers a secure verification link.'
      ),
      features: [
        {
          title: i18n('Modèles paramétrables', 'Configurable templates'),
          description: i18n(
            'Logo, mentions légales, QR code : tout s’adapte à votre charte et à vos obligations.',
            'Logo, legal mentions, QR code: everything adapts to your brand and obligations.'
          ),
        },
        {
          title: i18n('Signature et délivrance', 'Signing and delivery'),
          description: i18n(
            'Attestations signées électroniquement et envoyées automatiquement par email ou API.',
            'Electronically signed certificates sent automatically by email or API.'
          ),
        },
        {
          title: i18n('Portail de vérification', 'Verification portal'),
          description: i18n(
            'Tiers et financeurs vérifient l’authenticité en quelques secondes, sans intervention de votre équipe.',
            'Third parties and funders verify authenticity in seconds without involving your team.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 6 semaines', 'What you gain in 6 weeks'),
      subtitle: i18n(
        'La première cohorte pilote vous donnera un retour chiffré immédiat sur le temps administratif.',
        'The first pilot cohort gives you immediate quantitative feedback on admin time.'
      ),
      metrics: [
        { label: i18n('Temps de traitement', 'Processing time'), value: i18n('-80 %', '-80%') },
        { label: i18n('Erreurs documentaires', 'Document errors'), value: i18n('-90 %', '-90%') },
        { label: i18n('Délai de délivrance', 'Delivery delay'), value: i18n('< 1 h', '< 1h') },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n('Du template à la vérification', 'From template to verification'),
      subtitle: i18n(
        'Nous connectons votre source de vérité et testons la chaîne sur une session réelle.',
        'We connect your source of truth and test the chain on a real session.'
      ),
      steps: [
        {
          title: i18n('Mapping des données', 'Data mapping'),
          description: i18n(
            'Identification des champs nécessaires : apprenant, formation, dates, résultats.',
            'Identification of required fields: learner, training, dates, results.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('Générateur et signature', 'Generator and signing'),
          description: i18n(
            'Création du template PDF, intégration de la signature et tests de rendu.',
            'PDF template creation, signature integration, and rendering tests.'
          ),
          duration: i18n('Semaines 2-4', 'Weeks 2-4'),
        },
        {
          title: i18n('Portail et remise', 'Portal and handover'),
          description: i18n(
            'Mise en ligne du portail de vérification, documentation et transfert.',
            'Publication of verification portal, documentation, and handover.'
          ),
          duration: i18n('Semaines 5-6', 'Weeks 5-6'),
        },
      ],
    },
    cta: {
      title: i18n('Arrêtez de générer des attestations', 'Stop generating certificates'),
      highlightedTitle: i18n('à la main', 'manually'),
      description: i18n(
        'On vous montre comment automatiser la certification de votre prochaine session en 20 minutes.',
        'We’ll show you how to automate certification for your next session in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },
  {
    slug: 'connecteur-lms-crm',
    sectorSlug: 'education',
    image: 'https://placehold.co/1200x800/0f2137/b98745?text=LMS-CRM',
    imageAlt: i18n(
      'Données apprenants synchronisées entre LMS et CRM',
      'Learner data synchronized between LMS and CRM'
    ),
    northStarQuery: {
      sector: 'education',
      prompt: i18n(
        'Les données apprenants sont dispersées entre LMS et CRM',
        'Learner data is scattered between LMS and CRM'
      ),
    },
    seo: {
      title: i18n('MVP Connecteur LMS-CRM | Averiane', 'LMS-CRM connector MVP | Averiane'),
      description: i18n(
        'Unifiez les données de vos apprenants entre LMS et CRM pour améliorer le suivi commercial et pédagogique.',
        'Unify learner data between LMS and CRM to improve commercial and pedagogical follow-up.'
      ),
      keywords: i18nArr(
        ['LMS', 'CRM', 'connecteur', 'données apprenants', 'MVP éducation'],
        ['LMS', 'CRM', 'connector', 'learner data', 'education MVP']
      ),
    },
    hero: {
      eyebrow: i18n('Éducation & EdTech', 'Education & EdTech'),
      title: i18n('Connecteur', 'LMS-CRM'),
      highlightedTitle: i18n('LMS-CRM', 'connector'),
      description: i18n(
        'Synchronisez apprenants, inscriptions, progression et résultats entre votre LMS et votre CRM pour enfin avoir une vision 360°.',
        'Sync learners, enrollments, progress, and results between your LMS and CRM for a true 360° view.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n('Deux outils, une seule vérité manquante', 'Two tools, one missing truth'),
      subtitle: i18n(
        'Vos équipes commerciales ignorent la progression pédagogique ; vos formateurs ignorent le contexte commercial.',
        'Sales teams ignore learning progress; trainers ignore commercial context.'
      ),
      pains: [
        {
          title: i18n('Doublons et incohérences', 'Duplicates and inconsistencies'),
          description: i18n(
            'Un apprenant existe sous plusieurs emails, avec des informations contradictoires dans chaque outil.',
            'A learner exists under several emails with conflicting information in each tool.'
          ),
        },
        {
          title: i18n('Pas de visibilité commerciale', 'No commercial visibility'),
          description: i18n(
            'Le commercial ne sait pas si l’apprenant a terminé sa formation avant de relancer.',
            'The salesperson does not know whether the learner completed training before following up.'
          ),
        },
        {
          title: i18n('Reporting chronophage', 'Time-consuming reporting'),
          description: i18n(
            'Chaque tableau de bord exige un export Excel et des recoupements manuels.',
            'Every dashboard requires Excel exports and manual cross-checks.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un pont de données clé en main', 'A turnkey data bridge'),
      description: i18n(
        'Un connecteur fiable qui synchronise les contacts, les inscriptions, la progression et les résultats dans les deux sens.',
        'A reliable connector that syncs contacts, enrollments, progress, and results bidirectionally.'
      ),
      features: [
        {
          title: i18n('Synchronisation bidirectionnelle', 'Bidirectional sync'),
          description: i18n(
            'Mise à jour automatique des contacts et des inscriptions entre LMS et CRM.',
            'Automatic update of contacts and enrollments between LMS and CRM.'
          ),
        },
        {
          title: i18n('Rapports unifiés', 'Unified reports'),
          description: i18n(
            'Un dashboard simple croisant pipeline commercial et avancement pédagogique.',
            'A simple dashboard crossing sales pipeline and learning progress.'
          ),
        },
        {
          title: i18n('Règles de gestion', 'Business rules'),
          description: i18n(
            'Déduplication, mapping des statuts et alertes en cas d’anomalie.',
            'Deduplication, status mapping, and anomaly alerts.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 8 semaines', 'What you gain in 8 weeks'),
      subtitle: i18n(
        'Un seul référentiel et des équipes alignées dès le premier sprint.',
        'One reference source and aligned teams from the first sprint.'
      ),
      metrics: [
        { label: i18n('Temps de reporting', 'Reporting time'), value: i18n('-70 %', '-70%') },
        { label: i18n('Doublons éliminés', 'Duplicates removed'), value: i18n('-85 %', '-85%') },
        {
          label: i18n('Taux de relance ciblé', 'Targeted follow-up rate'),
          value: i18n('+40 %', '+40%'),
        },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n('De l’analyse à la synchronisation', 'From analysis to sync'),
      subtitle: i18n(
        'Nous cartographions vos deux systèmes puis nous synchronisons un sous-ensemble de données.',
        'We map your two systems, then sync a subset of data.'
      ),
      steps: [
        {
          title: i18n('Audit des deux systèmes', 'Audit both systems'),
          description: i18n(
            'Identification des objets, champs et règles de gestion à synchroniser.',
            'Identification of objects, fields, and business rules to sync.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('Connecteur et mapping', 'Connector and mapping'),
          description: i18n(
            'Développement du connecteur, tests de synchronisation et gestion des conflits.',
            'Connector development, sync tests, and conflict management.'
          ),
          duration: i18n('Semaines 2-5', 'Weeks 2-5'),
        },
        {
          title: i18n('Dashboard et transfert', 'Dashboard and handover'),
          description: i18n(
            'Unification des rapports, documentation et transfert à vos équipes.',
            'Unified reports, documentation, and handover to your teams.'
          ),
          duration: i18n('Semaines 6-8', 'Weeks 6-8'),
        },
      ],
    },
    cta: {
      title: i18n('Unifiez vos données', 'Unify your data'),
      highlightedTitle: i18n('apprenants', 'learner data'),
      description: i18n(
        'Discutons de votre LMS et de votre CRM. Nous vous montrons le MVP en 20 minutes.',
        'Let’s discuss your LMS and CRM. We’ll show you the MVP in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },

  // ─────────────────────────────────────────────────────────────
  // AGILE DELIVERY
  // ─────────────────────────────────────────────────────────────
  {
    slug: 'tableau-de-bord-de-delivery',
    sectorSlug: 'agile-delivery',
    image: 'https://ik.imagekit.io/lindahestia/articles/07-tableau-bord-delivery.webp?tr=f-webp',
    imageAlt: i18n('Tableau de bord de delivery projet', 'Project delivery dashboard'),
    northStarQuery: {
      sector: 'agile-delivery',
      prompt: i18n(
        "Le sponsor n'a pas de visibilité sur l'avancement réel du projet",
        'The sponsor has no visibility on the actual project progress'
      ),
    },
    seo: {
      title: i18n('MVP Tableau de bord Delivery | Averiane', 'Delivery dashboard MVP | Averiane'),
      description: i18n(
        'Un tableau de bord de delivery clair pour les sponsors : avancement réel, risques, blocages et décisions à prendre.',
        'A clear delivery dashboard for sponsors: real progress, risks, blockers, and decisions to make.'
      ),
      keywords: i18nArr(
        ['tableau de bord delivery', 'project dashboard', 'pilotage projet', 'MVP agile'],
        ['delivery dashboard', 'project dashboard', 'project steering', 'agile MVP']
      ),
    },
    hero: {
      eyebrow: i18n('Delivery Agile & Pilotage', 'Agile Delivery & Steering'),
      title: i18n('Tableau de bord', 'Delivery'),
      highlightedTitle: i18n('de delivery', 'dashboard'),
      description: i18n(
        'Donnez aux sponsors une vision simple et honnête de l’avancement : budget, délais, risques et décisions en un coup d’œil.',
        'Give sponsors a simple, honest view of progress: budget, deadlines, risks, and decisions at a glance.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n('Le sponsor est dans le brouillard', 'The sponsor is in the fog'),
      subtitle: i18n(
        'Des réunions de statut interminables, des slides jamais à jour, des décisions prises sans visibilité sur les vrais blocages.',
        'Endless status meetings, never-updated slides, decisions made without visibility on real blockers.'
      ),
      pains: [
        {
          title: i18n('Statut biaisé', 'Biased status'),
          description: i18n(
            'Chaque équipe présente sa version de la vérité, rendant impossible la mesure de l’avancement réel.',
            'Each team presents its own version of the truth, making real progress impossible to measure.'
          ),
        },
        {
          title: i18n('Décisions trop tardives', 'Late decisions'),
          description: i18n(
            'Les risques apparaissent quand il est déjà trop cher de les traiter sereinement.',
            'Risks appear when it is already too expensive to handle them calmly.'
          ),
        },
        {
          title: i18n('Outils trop techniques', 'Overly technical tools'),
          description: i18n(
            'Jira et autres outils sont conçus pour les équipes, pas pour un comité de direction.',
            'Jira and similar tools are built for teams, not for an executive committee.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un cockpit de pilotage exécutif', 'An executive steering cockpit'),
      description: i18n(
        'Le MVP agrège les données techniques, budgetaires et humaines dans un dashboard lisible en 30 secondes.',
        'The MVP aggregates technical, budget, and human data into a dashboard readable in 30 seconds.'
      ),
      features: [
        {
          title: i18n('North Star et indicateurs clairs', 'North Star and clear KPIs'),
          description: i18n(
            'Une métrique principale et 3 indicateurs d’accompagnement choisis avec le sponsor.',
            'One primary metric and 3 supporting indicators chosen with the sponsor.'
          ),
        },
        {
          title: i18n('Alertes automatisées', 'Automated alerts'),
          description: i18n(
            'Détection des dérives de budget, de délai ou de qualité avant qu’elles ne deviennent critiques.',
            'Detection of budget, deadline, or quality drift before they become critical.'
          ),
        },
        {
          title: i18n('Compte-rendu hebdomadaire', 'Weekly report'),
          description: i18n(
            'Un récapitulatif automatique partageable avec les parties prenantes.',
            'An automatically shareable summary for stakeholders.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 6 semaines', 'What you gain in 6 weeks'),
      subtitle: i18n(
        'Des comités de direction plus courts et des décisions prises plus tôt.',
        'Shorter executive meetings and earlier decisions.'
      ),
      metrics: [
        { label: i18n('Durée des comités', 'Meeting duration'), value: i18n('-50 %', '-50%') },
        { label: i18n('Décisions anticipées', 'Early decisions'), value: i18n('+60 %', '+60%') },
        {
          label: i18n('Visibilité sponsor', 'Sponsor visibility'),
          value: i18n('Temps réel', 'Real-time'),
        },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n('Des données brutes au cockpit', 'From raw data to cockpit'),
      subtitle: i18n(
        'Nous connectons vos sources existantes et concevons le dashboard autour de vos décisions réelles.',
        'We connect your existing sources and design the dashboard around your real decisions.'
      ),
      steps: [
        {
          title: i18n('Atelier de cadrage', 'Framing workshop'),
          description: i18n(
            'Définition de la North Star, des décisions à éclairer et des sources de données.',
            'Define the North Star, decisions to illuminate, and data sources.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('MVP dashboard', 'Dashboard MVP'),
          description: i18n(
            'Connexion des données et création des premiers graphiques et alertes.',
            'Data connection and creation of first charts and alerts.'
          ),
          duration: i18n('Semaines 2-4', 'Weeks 2-4'),
        },
        {
          title: i18n('Déploiement et adoption', 'Rollout and adoption'),
          description: i18n(
            'Formation du sponsor, ajustements et documentation des indicateurs.',
            'Sponsor training, adjustments, and indicator documentation.'
          ),
          duration: i18n('Semaines 5-6', 'Weeks 5-6'),
        },
      ],
    },
    cta: {
      title: i18n('Donnez de la visibilité', 'Bring visibility'),
      highlightedTitle: i18n('à vos sponsors', 'to your sponsors'),
      description: i18n(
        'On vous montre comment un MVP dashboard peut transformer vos comités de pilotage en 20 minutes.',
        'We’ll show you how a dashboard MVP can transform your steering committees in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },
  {
    slug: 'rituel-de-priorisation-partage',
    sectorSlug: 'agile-delivery',
    image:
      'https://ik.imagekit.io/lindahestia/articles/08-rituel-priorisation-partage.webp?tr=f-webp',
    imageAlt: i18n(
      'Atelier de priorisation entre métiers et technique',
      'Prioritization workshop between business and engineering'
    ),
    northStarQuery: {
      sector: 'agile-delivery',
      prompt: i18n(
        'Les équipes métier et technique ne sont pas alignées sur les priorités',
        'Business and engineering teams are not aligned on priorities'
      ),
    },
    seo: {
      title: i18n(
        'MVP Rituel de priorisation | Averiane',
        'Shared prioritization ritual MVP | Averiane'
      ),
      description: i18n(
        'Installez un rituel de priorisation partagé pour aligner les équipes métier et technique sur les enjeux réels.',
        'Install a shared prioritization ritual to align business and engineering teams on real issues.'
      ),
      keywords: i18nArr(
        ['rituel priorisation', 'alignment équipe', 'MVP agile', 'product management'],
        ['prioritization ritual', 'team alignment', 'agile MVP', 'product management']
      ),
    },
    hero: {
      eyebrow: i18n('Delivery Agile & Pilotage', 'Agile Delivery & Steering'),
      title: i18n('Rituel de', 'Shared'),
      highlightedTitle: i18n('priorisation partagé', 'prioritization ritual'),
      description: i18n(
        'Un outil et une méthode pour décider ensemble, chaque semaine, de ce qui crée le plus de valeur et pourquoi.',
        'A tool and method to decide together, every week, what creates the most value and why.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n(
        'Métier et tech ne décident pas ensemble',
        'Business and tech do not decide together'
      ),
      subtitle: i18n(
        'Chaque camp défend son backlog. Les sujets stratégiques sont noyés sous les urgences et les dettes techniques.',
        'Each side defends its own backlog. Strategic topics drown under urgencies and technical debt.'
      ),
      pains: [
        {
          title: i18n('Priorités contradictoires', 'Conflicting priorities'),
          description: i18n(
            'Le métier veut des fonctionnalités, la tech veut de la qualité : le dialogue tourne en rond.',
            'Business wants features, tech wants quality: the dialogue goes in circles.'
          ),
        },
        {
          title: i18n('Décisions informelles', 'Informal decisions'),
          description: i18n(
            'Les arbitrages se prennent dans des couloirs, sans traçabilité ni alignement collectif.',
            'Trade-offs happen in hallways, with no traceability or collective alignment.'
          ),
        },
        {
          title: i18n('Livrables déconnectés de la valeur', 'Deliverables disconnected from value'),
          description: i18n(
            'Des fonctionnalités sortent mais personne ne mesure si elles résolvent le bon problème.',
            'Features ship but no one measures whether they solve the right problem.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un rituel structuré et documenté', 'A structured, documented ritual'),
      description: i18n(
        'Le MVP fournit un template de scoring commun (valeur, risque, effort) et un espace de décision partagé.',
        'The MVP provides a common scoring template (value, risk, effort) and a shared decision space.'
      ),
      features: [
        {
          title: i18n('Scoring partagé', 'Shared scoring'),
          description: i18n(
            'Chaque sujet est noté selon les mêmes critères par le métier et la tech.',
            'Each topic is scored by business and tech using the same criteria.'
          ),
        },
        {
          title: i18n('Trace des décisions', 'Decision traceability'),
          description: i18n(
            'Pourquoi on fait tel choix ? La justification est conservée et visible pour toute l’équipe.',
            'Why this choice? The rationale is kept and visible to the whole team.'
          ),
        },
        {
          title: i18n('Rappels et actions', 'Reminders and actions'),
          description: i18n(
            'Export vers les outils de suivi et notifications des décisions à réexaminer.',
            'Export to tracking tools and notifications for decisions to revisit.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 6 semaines', 'What you gain in 6 weeks'),
      subtitle: i18n(
        'Des réunions productives et des arbitrages assumés collectivement.',
        'Productive meetings and collectively owned trade-offs.'
      ),
      metrics: [
        { label: i18n('Conflits de priorité', 'Priority conflicts'), value: i18n('-60 %', '-60%') },
        { label: i18n('Temps de réunion', 'Meeting time'), value: i18n('-40 %', '-40%') },
        { label: i18n('Alignement équipes', 'Team alignment'), value: i18n('+45 %', '+45%') },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n('De la méthode à l’outil', 'From method to tool'),
      subtitle: i18n(
        'On cale le rituel avec vos équipes puis on l’outille avec un MVP léger.',
        'We align the ritual with your teams, then equip it with a lightweight MVP.'
      ),
      steps: [
        {
          title: i18n('Atelier de définition', 'Definition workshop'),
          description: i18n(
            'Définition des critères de scoring, des rôles et du rythme du rituel.',
            'Define scoring criteria, roles, and ritual cadence.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('MVP outillage', 'Tooling MVP'),
          description: i18n(
            'Création du template, espace collaboratif et premières sessions pilotes.',
            'Template creation, collaborative space, and first pilot sessions.'
          ),
          duration: i18n('Semaines 2-4', 'Weeks 2-4'),
        },
        {
          title: i18n('Routine et amélioration', 'Routine and improvement'),
          description: i18n(
            'Ajustements après 3 à 5 itérations, documentation et transfert.',
            'Adjustments after 3-5 iterations, documentation, and handover.'
          ),
          duration: i18n('Semaines 5-6', 'Weeks 5-6'),
        },
      ],
    },
    cta: {
      title: i18n('Alignez métier et technique', 'Align business and tech'),
      highlightedTitle: i18n('sur les priorités', 'on priorities'),
      description: i18n(
        'On vous accompagne à définir et outiller votre rituel de priorisation en 20 minutes.',
        'We’ll help you define and tool your prioritization ritual in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },
  {
    slug: 'health-check-delivery',
    sectorSlug: 'agile-delivery',
    image: 'https://placehold.co/1200x800/0d2842/b98745?text=Health-check',
    imageAlt: i18n('Health-check de delivery projet', 'Delivery health-check'),
    northStarQuery: {
      sector: 'operations',
      prompt: i18n(
        'Je ne sais pas si mon delivery est sain ou où prioriser',
        "I don't know if my delivery is healthy or where to prioritize"
      ),
    },
    seo: {
      title: i18n('MVP Health-check Delivery | Averiane', 'Delivery health-check MVP | Averiane'),
      description: i18n(
        'Un MVP de health-check delivery pour évaluer rapidement la santé de votre projet et identifier où agir en priorité.',
        'A delivery health-check MVP to quickly assess your project health and identify where to act first.'
      ),
      keywords: i18nArr(
        ['health-check', 'delivery', 'audit projet', 'MVP agile', 'pilotage'],
        ['health-check', 'delivery', 'project audit', 'agile MVP', 'steering']
      ),
    },
    hero: {
      eyebrow: i18n('Delivery Agile & Pilotage', 'Agile Delivery & Steering'),
      title: i18n('Health-check', 'Delivery'),
      highlightedTitle: i18n('de delivery', 'health-check'),
      description: i18n(
        'Évaluez la santé de votre delivery en 12 questions, obtenez un score et un plan d’action priorisé en 48 h.',
        'Assess your delivery health in 12 questions, get a score and a prioritized action plan in 48 hours.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n('Où se situent les vrais risques ?', 'Where are the real risks?'),
      subtitle: i18n(
        'Les indicateurs techniques existent, mais aucun ne dit si le delivery dans son ensemble est sain.',
        'Technical indicators exist, but none say whether the delivery as a whole is healthy.'
      ),
      pains: [
        {
          title: i18n('Sous-feuillage d’indicateurs', 'Indicator overload'),
          description: i18n(
            'Trop de métriques, pas de synthèse : on ne sait plus ce qui compte vraiment.',
            'Too many metrics, no synthesis: it is unclear what really matters.'
          ),
        },
        {
          title: i18n('Blind spots', 'Blind spots'),
          description: i18n(
            'Les risques humains, organisationnels et commerciaux sont ignorés jusqu’au dérapage.',
            'Human, organizational, and commercial risks are ignored until it is too late.'
          ),
        },
        {
          title: i18n('Absence de plan d’action', 'No action plan'),
          description: i18n(
            'Quand on découvre un problème, on ne sait pas par où commencer ni qui en est responsable.',
            'When a problem is found, no one knows where to start or who is responsible.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un diagnostic standardisé et rapide', 'A standardized, fast diagnosis'),
      description: i18n(
        'Le MVP pose les bonnes questions, agrège les réponses et génère un score avec des recommandations priorisées.',
        'The MVP asks the right questions, aggregates answers, and generates a score with prioritized recommendations.'
      ),
      features: [
        {
          title: i18n('Questionnaire ciblé', 'Targeted questionnaire'),
          description: i18n(
            '12 questions sur valeur, délais, qualité, équipe et sponsor, complétées en 15 min.',
            '12 questions on value, deadlines, quality, team, and sponsor, completed in 15 min.'
          ),
        },
        {
          title: i18n('Score et radar', 'Score and radar'),
          description: i18n(
            'Une visualisation simple des forces et faiblesses du delivery.',
            'A simple visualization of delivery strengths and weaknesses.'
          ),
        },
        {
          title: i18n('Plan d’action priorisé', 'Prioritized action plan'),
          description: i18n(
            '3 actions concrètes classées par impact/effort, avec un responsable et une échéance.',
            '3 concrete actions ranked by impact/effort, with an owner and a deadline.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 4 semaines', 'What you gain in 4 weeks'),
      subtitle: i18n(
        'Un premier diagnostic et un plan d’action pour stabiliser ou accélérer votre delivery.',
        'A first diagnosis and action plan to stabilize or accelerate your delivery.'
      ),
      metrics: [
        { label: i18n('Temps de diagnostic', 'Diagnosis time'), value: i18n('-70 %', '-70%') },
        { label: i18n('Risques identifiés', 'Risks identified'), value: i18n('+3×', '3×') },
        {
          label: i18n('Décisions d’arbitrage', 'Trade-off decisions'),
          value: i18n('+50 %', '+50%'),
        },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n('Du questionnaire au plan d’action', 'From questionnaire to action plan'),
      subtitle: i18n(
        'Nous construisons le health-check autour de votre contexte puis nous le testons avec vos équipes.',
        'We build the health-check around your context, then test it with your teams.'
      ),
      steps: [
        {
          title: i18n('Définition des 12 questions', 'Define the 12 questions'),
          description: i18n(
            'Sélection des dimensions clés et validation avec le sponsor.',
            'Selection of key dimensions and validation with the sponsor.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('Outil et scoring', 'Tool and scoring'),
          description: i18n(
            'Formulaire, calcul du score, radar et génération du plan d’action.',
            'Form, score calculation, radar, and action plan generation.'
          ),
          duration: i18n('Semaines 2-3', 'Weeks 2-3'),
        },
        {
          title: i18n('Premier diagnostic', 'First diagnosis'),
          description: i18n(
            'Session pilote avec les équipes, ajustements et transfert.',
            'Pilot session with teams, adjustments, and handover.'
          ),
          duration: i18n('Semaine 4', 'Week 4'),
        },
      ],
    },
    cta: {
      title: i18n('Faites le point', 'Take stock'),
      highlightedTitle: i18n('sur votre delivery', 'of your delivery'),
      description: i18n(
        'On vous propose un health-check rapide et un plan d’action en 20 minutes.',
        'We propose a quick health-check and action plan in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },

  // ─────────────────────────────────────────────────────────────
  // BIOLOGIE & LIFE SCIENCES
  // ─────────────────────────────────────────────────────────────
  {
    slug: 'portail-de-metadata-cliniques',
    sectorSlug: 'biology',
    image:
      'https://ik.imagekit.io/lindahestia/articles/01-portail-metadonnees-cliniques.webp?tr=f-webp',
    imageAlt: i18n('Portail de metadata cliniques', 'Clinical metadata portal'),
    northStarQuery: {
      sector: 'biology',
      prompt: i18n(
        'La gestion des metadata cliniques est dispersée et source d’erreurs',
        'Clinical metadata management is scattered and error-prone'
      ),
    },
    seo: {
      title: i18n(
        'MVP Portail metadata cliniques | Averiane',
        'Clinical metadata portal MVP | Averiane'
      ),
      description: i18n(
        'Centralisez les metadata cliniques dans un portail unique, traçable et accessible par les équipes de recherche.',
        'Centralize clinical metadata in a single traceable portal accessible by research teams.'
      ),
      keywords: i18nArr(
        ['metadata cliniques', 'portail metadata', 'MVP biologie', 'R&D', 'data management'],
        ['clinical metadata', 'metadata portal', 'biology MVP', 'R&D', 'data management']
      ),
    },
    hero: {
      eyebrow: i18n('Biologie & Life Sciences', 'Biology & Life Sciences'),
      title: i18n('Portail de', 'Clinical metadata'),
      highlightedTitle: i18n('metadata cliniques', 'portal'),
      description: i18n(
        'Un point d’accès unique pour vos metadata cliniques : saisie contrôlée, historique des modifications et traçabilité pour les audits.',
        'A single access point for your clinical metadata: controlled entry, change history, and audit traceability.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n('Des metadata éparpillées et peu fiables', 'Scattered and unreliable metadata'),
      subtitle: i18n(
        'Excel, emails et fichiers locaux : chaque étude réinvente son système, multipliant les erreurs et les retards.',
        'Excel, emails, and local files: every study reinvents its system, multiplying errors and delays.'
      ),
      pains: [
        {
          title: i18n('Versions contradictoires', 'Conflicting versions'),
          description: i18n(
            'Plusieurs fichiers circulent en parallèle, sans version de référence ni contrôle d’accès.',
            'Several files circulate in parallel with no reference version or access control.'
          ),
        },
        {
          title: i18n('Erreurs de saisie coûteuses', 'Costly input errors'),
          description: i18n(
            'Une metadata mal renseignée peut invalider une analyse ou retarder une soumission réglementaire.',
            'One incorrectly entered metadata point can invalidate an analysis or delay a regulatory submission.'
          ),
        },
        {
          title: i18n('Traçabilité insuffisante', 'Insufficient traceability'),
          description: i18n(
            'Les audits demandent qui a modifié quoi et quand : sans historique, la réponse est impossible.',
            'Audits ask who changed what and when: without history, the answer is impossible.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un portail central et traçable', 'A central, traceable portal'),
      description: i18n(
        'Le MVP propose un formulaire structuré, un référentiel unique et un historique complet des modifications.',
        'The MVP offers a structured form, a single repository, and a complete change history.'
      ),
      features: [
        {
          title: i18n('Formulaire validé', 'Validated form'),
          description: i18n(
            'Contraintes de format, vocabulaires contrôlés et champs obligatoires réduisent les erreurs de saisie.',
            'Format constraints, controlled vocabularies, and mandatory fields reduce input errors.'
          ),
        },
        {
          title: i18n('Historique complet', 'Full history'),
          description: i18n(
            'Chaque modification est horodatée, signée et justifiée pour les audits.',
            'Every change is timestamped, signed, and justified for audits.'
          ),
        },
        {
          title: i18n('Exports réglementaires', 'Regulatory exports'),
          description: i18n(
            'Génération de fichiers prêts pour les dépôts et les partages avec les partenaires.',
            'Generation of files ready for submissions and sharing with partners.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 8 semaines', 'What you gain in 8 weeks'),
      subtitle: i18n(
        'Une metadata claire dès le début de la prochaine étude et un gain de temps pour les data managers.',
        'Clear metadata from the start of the next study and time savings for data managers.'
      ),
      metrics: [
        { label: i18n('Erreurs de metadata', 'Metadata errors'), value: i18n('-70 %', '-70%') },
        {
          label: i18n('Temps de préparation audit', 'Audit prep time'),
          value: i18n('-50 %', '-50%'),
        },
        { label: i18n('Partage équipes', 'Team sharing'), value: i18n('+4×', '4×') },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n('Du modèle de données au portail', 'From data model to portal'),
      subtitle: i18n(
        'Nous modélisons vos metadata puis nous livrons un portail opérationnel pour une étude pilote.',
        'We model your metadata, then deliver an operational portal for a pilot study.'
      ),
      steps: [
        {
          title: i18n('Modélisation des metadata', 'Metadata modeling'),
          description: i18n(
            'Identification des entités, attributs, vocabulaires et règles de validation.',
            'Identification of entities, attributes, vocabularies, and validation rules.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('Portail et workflow', 'Portal and workflow'),
          description: i18n(
            'Développement du portail, formulaires, rôles et historique des modifications.',
            'Portal development, forms, roles, and change history.'
          ),
          duration: i18n('Semaines 2-6', 'Weeks 2-6'),
        },
        {
          title: i18n('Validation sur étude pilote', 'Pilot study validation'),
          description: i18n(
            'Test avec vos data managers, ajustements et documentation.',
            'Test with your data managers, adjustments, and documentation.'
          ),
          duration: i18n('Semaines 7-8', 'Weeks 7-8'),
        },
      ],
    },
    cta: {
      title: i18n('Centralisez vos metadata', 'Centralize your metadata'),
      highlightedTitle: i18n('cliniques', 'clinical'),
      description: i18n(
        'On vous montre comment un portail MVP peut fiabiliser vos études en 20 minutes.',
        'We’ll show you how an MVP portal can make your studies more reliable in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },
  {
    slug: 'pipeline-donnees-reproductible',
    sectorSlug: 'biology',
    image:
      'https://ik.imagekit.io/lindahestia/articles/02-pipeline-donnees-reproductible.webp?tr=f-webp',
    imageAlt: i18n(
      'Pipeline de données reproductible pour laboratoire',
      'Reproducible data pipeline for laboratory'
    ),
    northStarQuery: {
      sector: 'biology',
      prompt: i18n(
        'Les pipelines de données sont fragiles et non reproductibles',
        'Data pipelines are fragile and non-reproducible'
      ),
    },
    seo: {
      title: i18n(
        'MVP Pipeline de données reproductible | Averiane',
        'Reproducible data pipeline MVP | Averiane'
      ),
      description: i18n(
        'Un MVP de pipeline de données reproductible pour laboratoires : versionning, traçabilité et exécution fiable.',
        'An MVP reproducible data pipeline for labs: versioning, traceability, and reliable execution.'
      ),
      keywords: i18nArr(
        ['pipeline données', 'reproductibilité', 'MVP biologie', 'bio-informatique', 'laboratoire'],
        ['data pipeline', 'reproducibility', 'biology MVP', 'bioinformatics', 'laboratory']
      ),
    },
    hero: {
      eyebrow: i18n('Biologie & Life Sciences', 'Biology & Life Sciences'),
      title: i18n('Pipeline de données', 'Reproducible data'),
      highlightedTitle: i18n('reproductible', 'pipeline'),
      description: i18n(
        'Transformez votre chaîne de traitement en un pipeline versionné, testé et reproductible, de l’import à l’analyse.',
        'Turn your processing chain into a versioned, tested, reproducible pipeline from import to analysis.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n('Des résultats qu’on ne peut pas reproduire', 'Results you cannot reproduce'),
      subtitle: i18n(
        'Scripts locaux, dépendances non documentées, paramètres oubliés : chaque analyse est une nouvelle aventure.',
        'Local scripts, undocumented dependencies, forgotten parameters: every analysis is a new adventure.'
      ),
      pains: [
        {
          title: i18n('Scripts éparpillés', 'Scattered scripts'),
          description: i18n(
            'Chaque membre de l’équipe a sa propre version, sans revue ni validation commune.',
            'Each team member has their own version, with no review or common validation.'
          ),
        },
        {
          title: i18n('Dépendances non maîtrisées', 'Uncontrolled dependencies'),
          description: i18n(
            'Un package mis à jour ou un environnement différent change silencieusement les résultats.',
            'One updated package or different environment silently changes results.'
          ),
        },
        {
          title: i18n('Peer-review difficile', 'Difficult peer review'),
          description: i18n(
            'Impossible de revoir exactement comment un résultat a été obtenu.',
            'Impossible to review exactly how a result was obtained.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un pipeline versionné et documenté', 'A versioned, documented pipeline'),
      description: i18n(
        'Le MVP encode votre workflow en étapes reproductibles, avec gestion des versions, environnement figé et logs d’exécution.',
        'The MVP encodes your workflow into reproducible steps with versioning, pinned environment, and execution logs.'
      ),
      features: [
        {
          title: i18n('Workflow structuré', 'Structured workflow'),
          description: i18n(
            'Import, nettoyage, transformation et analyse découpés en étapes testables individuellement.',
            'Import, cleaning, transformation, and analysis split into individually testable steps.'
          ),
        },
        {
          title: i18n('Environnement reproductible', 'Reproducible environment'),
          description: i18n(
            'Containerisation et gestion des dépendances pour garantir le même résultat partout.',
            'Containerization and dependency management to guarantee the same result everywhere.'
          ),
        },
        {
          title: i18n('Traçabilité des exécutions', 'Execution traceability'),
          description: i18n(
            'Chaque run conserve ses paramètres, versions et résultats pour une revue complète.',
            'Each run keeps its parameters, versions, and results for full review.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 8 semaines', 'What you gain in 8 weeks'),
      subtitle: i18n(
        'La reproductibilité devient la norme pour vos analyses critiques.',
        'Reproducibility becomes the norm for your critical analyses.'
      ),
      metrics: [
        {
          label: i18n('Temps de reprise d’analyse', 'Analysis restart time'),
          value: i18n('-70 %', '-70%'),
        },
        {
          label: i18n('Erreurs de reproductibilité', 'Reproducibility errors'),
          value: i18n('-80 %', '-80%'),
        },
        { label: i18n('Temps de revue', 'Review time'), value: i18n('-50 %', '-50%') },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n('Du script au pipeline', 'From script to pipeline'),
      subtitle: i18n(
        'Nous prenons un workflow représentatif et le transformons en pipeline robuste.',
        'We take a representative workflow and turn it into a robust pipeline.'
      ),
      steps: [
        {
          title: i18n('Inventaire et priorisation', 'Inventory and prioritization'),
          description: i18n(
            'Identification du workflow le plus critique et des points de fragilité.',
            'Identification of the most critical workflow and fragility points.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('Développement du pipeline', 'Pipeline development'),
          description: i18n(
            'Encodage des étapes, tests, containerisation et documentation.',
            'Encoding steps, tests, containerization, and documentation.'
          ),
          duration: i18n('Semaines 2-6', 'Weeks 2-6'),
        },
        {
          title: i18n('Validation par les équipes', 'Team validation'),
          description: i18n(
            'Exécutions pilotes, comparaison avec l’ancien workflow et transfert.',
            'Pilot runs, comparison with the old workflow, and handover.'
          ),
          duration: i18n('Semaines 7-8', 'Weeks 7-8'),
        },
      ],
    },
    cta: {
      title: i18n('Rendez vos analyses', 'Make your analyses'),
      highlightedTitle: i18n('reproductibles', 'reproducible'),
      description: i18n(
        'On vous montre comment fiabiliser un pipeline critique en 20 minutes.',
        'We’ll show you how to make a critical pipeline reliable in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },
  {
    slug: 'cockpit-qualite-des-donnees',
    sectorSlug: 'biology',
    image: 'https://placehold.co/1200x800/0f2137/b98745?text=Data+Quality',
    imageAlt: i18n('Cockpit qualité des données de recherche', 'Research data quality cockpit'),
    northStarQuery: {
      sector: 'pharma',
      prompt: i18n(
        'La qualité des données de recherche est difficile à contrôler',
        'Research data quality is hard to control'
      ),
    },
    seo: {
      title: i18n(
        'MVP Cockpit qualité des données | Averiane',
        'Lab data quality cockpit MVP | Averiane'
      ),
      description: i18n(
        'Un cockpit de qualité des données pour suivre la complétude, la cohérence et la conformité de vos données de recherche.',
        'A data quality cockpit to track completeness, consistency, and compliance of your research data.'
      ),
      keywords: i18nArr(
        ['qualité des données', 'cockpit data quality', 'MVP biologie', 'R&D', 'conformité'],
        ['data quality', 'data quality cockpit', 'biology MVP', 'R&D', 'compliance']
      ),
    },
    hero: {
      eyebrow: i18n('Biologie & Life Sciences', 'Biology & Life Sciences'),
      title: i18n('Cockpit qualité', 'Lab data quality'),
      highlightedTitle: i18n('des données', 'cockpit'),
      description: i18n(
        'Visualisez la santé de vos données en temps réel : complétude, cohérence et conformité, avec des alertes avant que les erreurs ne polluent l’analyse.',
        'Visualize your data health in real time: completeness, consistency, and compliance, with alerts before errors pollute analysis.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n(
        'Des problèmes de données découverts trop tard',
        'Data issues discovered too late'
      ),
      subtitle: i18n(
        'Les anomalies ne sont détectées qu’en phase d’analyse, quand elles coûtent cher en temps et en crédibilité.',
        'Anomalies are only detected at analysis phase, when they cost time and credibility.'
      ),
      pains: [
        {
          title: i18n('Contrôles manuels', 'Manual checks'),
          description: i18n(
            'Des scripts ad-hoc et des vérifications Excel répétées à chaque nouvelle livraison.',
            'Ad-hoc scripts and repeated Excel checks on every new delivery.'
          ),
        },
        {
          title: i18n('Alertes trop tardives', 'Late alerts'),
          description: i18n(
            'Les data managers découvrent les incohérences après consolidation, pas à la source.',
            'Data managers discover inconsistencies after consolidation, not at the source.'
          ),
        },
        {
          title: i18n('Pas de vue d’ensemble', 'No overall view'),
          description: i18n(
            'Impossible de dire rapidement “nos données sont-elles assez saines pour l’analyse ?”',
            'Impossible to quickly say, “are our data healthy enough for analysis?”'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un cockpit de contrôle qualité', 'A quality control cockpit'),
      description: i18n(
        'Le MVP définit des règles de qualité, les exécute automatiquement et présente les résultats dans un dashboard clair.',
        'The MVP defines quality rules, runs them automatically, and presents results in a clear dashboard.'
      ),
      features: [
        {
          title: i18n('Règles de qualité configurables', 'Configurable quality rules'),
          description: i18n(
            'Complétude, plages de valeurs, cohérence entre tables : vous définissez ce qui compte.',
            'Completeness, value ranges, cross-table consistency: you define what matters.'
          ),
        },
        {
          title: i18n('Dashboard temps réel', 'Real-time dashboard'),
          description: i18n(
            'Score global de qualité, indicateurs par étude et liste des anomalies à traiter.',
            'Overall quality score, per-study indicators, and list of anomalies to address.'
          ),
        },
        {
          title: i18n('Alertes et responsables', 'Alerts and owners'),
          description: i18n(
            'Notifications vers les data managers avec lien direct vers les lignes concernées.',
            'Notifications to data managers with direct links to affected rows.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 6 semaines', 'What you gain in 6 weeks'),
      subtitle: i18n(
        'La qualité des données devient un indicateur suivi, pas une surprise de dernière minute.',
        'Data quality becomes a tracked indicator, not a last-minute surprise.'
      ),
      metrics: [
        {
          label: i18n('Anomalies détectées tôt', 'Anomalies detected early'),
          value: i18n('+3×', '3×'),
        },
        { label: i18n('Temps de nettoyage', 'Cleaning time'), value: i18n('-50 %', '-50%') },
        { label: i18n('Taux de données valides', 'Valid data rate'), value: i18n('+25 %', '+25%') },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n('Des règles au cockpit', 'From rules to cockpit'),
      subtitle: i18n(
        'Nous identifions vos règles de qualité critiques puis nous les industrialisons dans le MVP.',
        'We identify your critical quality rules, then industrialize them in the MVP.'
      ),
      steps: [
        {
          title: i18n('Atelier qualité', 'Quality workshop'),
          description: i18n(
            'Recensement des règles actuelles et prioritisation des plus impactantes.',
            'Inventory of current rules and prioritization of the most impactful ones.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('Cockpit MVP', 'MVP cockpit'),
          description: i18n(
            'Développement du moteur de règles, dashboard et système d’alertes.',
            'Rule engine, dashboard, and alert system development.'
          ),
          duration: i18n('Semaines 2-4', 'Weeks 2-4'),
        },
        {
          title: i18n('Test sur étude pilote', 'Pilot study test'),
          description: i18n(
            'Validation des règles sur des données réelles, ajustements et transfert.',
            'Rule validation on real data, adjustments, and handover.'
          ),
          duration: i18n('Semaines 5-6', 'Weeks 5-6'),
        },
      ],
    },
    cta: {
      title: i18n('Prenez le contrôle', 'Take control'),
      highlightedTitle: i18n('de la qualité', 'of data quality'),
      description: i18n(
        'On vous montre comment surveiller la qualité de vos données en temps réel en 20 minutes.',
        'We’ll show you how to monitor your data quality in real time in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },

  // ─────────────────────────────────────────────────────────────
  // LOGISTIQUE & SUPPLY CHAIN
  // ─────────────────────────────────────────────────────────────
  {
    slug: 'wms-leger-connecte-a-l-erp',
    sectorSlug: 'logistics',
    image: 'https://ik.imagekit.io/lindahestia/articles/03-wms-connecte-erp.webp?tr=f-webp',
    imageAlt: i18n('WMS léger connecté à l’ERP', 'Lightweight WMS connected to ERP'),
    northStarQuery: {
      sector: 'logistics',
      prompt: i18n(
        "La gestion d'entrepôt repose sur des fichiers Excel et des processus manuels",
        'Warehouse management relies on Excel files and manual processes'
      ),
    },
    seo: {
      title: i18n(
        'MVP WMS léger connecté ERP | Averiane',
        'Lightweight WMS connected to ERP MVP | Averiane'
      ),
      description: i18n(
        'Un WMS léger connecté à votre ERP pour digitaliser la réception, le stockage et l’expédition sans remplacer votre système.',
        'A lightweight WMS connected to your ERP to digitize receiving, storage, and shipping without replacing your system.'
      ),
      keywords: i18nArr(
        ['WMS léger', 'ERP', 'entrepôt', 'MVP logistique', 'supply chain'],
        ['lightweight WMS', 'ERP', 'warehouse', 'logistics MVP', 'supply chain']
      ),
    },
    hero: {
      eyebrow: i18n('Logistique & Supply Chain', 'Logistics & Supply Chain'),
      title: i18n('WMS léger', 'Lightweight WMS'),
      highlightedTitle: i18n('connecté à l’ERP', 'connected to ERP'),
      description: i18n(
        'Un MVP qui digitalise les opérations d’entrepôt et synchronise les stocks avec votre ERP, sans changer de système lourd.',
        'An MVP that digitizes warehouse operations and syncs inventory with your ERP, without changing your heavy system.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n('L’entrepôt tourne à l’Excel', 'The warehouse runs on Excel'),
      subtitle: i18n(
        'Bons de réception, inventaires et bons d’expédition sont gérés à la main : erreurs, retards et stocks incohérents.',
        'Receiving notes, inventories, and shipping orders are handled manually: errors, delays, and inconsistent stock.'
      ),
      pains: [
        {
          title: i18n('Saisies manuelles', 'Manual entries'),
          description: i18n(
            'Chaque mouvement est reporté dans Excel, avec des fautes de saisie et des oublis fréquents.',
            'Every movement is entered into Excel, with frequent typos and omissions.'
          ),
        },
        {
          title: i18n('Stock non fiable', 'Unreliable stock'),
          description: i18n(
            'L’ERP affiche une quantité, l’entrepôt en a une autre. Les commandes clients en pâtissent.',
            'The ERP shows one quantity, the warehouse another. Customer orders suffer.'
          ),
        },
        {
          title: i18n('Temps de recherche', 'Search time'),
          description: i18n(
            'Les opérateurs perdent du temps à chercher où est un produit ou à vérifier un bon.',
            'Operators waste time searching for a product or verifying an order.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un WMS MVP qui complète votre ERP', 'A WMS MVP that complements your ERP'),
      description: i18n(
        'Interface mobile pour les opérateurs, synchronisation des stocks et traçabilité des mouvements en temps réel.',
        'Mobile interface for operators, inventory sync, and real-time movement traceability.'
      ),
      features: [
        {
          title: i18n('Interface opérateur mobile', 'Mobile operator interface'),
          description: i18n(
            'Réception, mise en stock, préparation et expédition guidées par scan ou saisie simple.',
            'Receiving, put-away, picking, and shipping guided by scan or simple input.'
          ),
        },
        {
          title: i18n('Sync ERP bidirectionnelle', 'Bidirectional ERP sync'),
          description: i18n(
            'Les stocks et commandes sont à jour dans les deux sens, sans double saisie.',
            'Stock and orders stay up to date both ways, with no double entry.'
          ),
        },
        {
          title: i18n('Traçabilité des mouvements', 'Movement traceability'),
          description: i18n(
            'Chaque action est horodatée et associée à un opérateur pour réduire les litiges.',
            'Every action is timestamped and tied to an operator to reduce disputes.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 8 semaines', 'What you gain in 8 weeks'),
      subtitle: i18n(
        'Un entrepôt pilote opérationnel avec des opérateurs formés et un ERP synchronisé.',
        'An operational pilot warehouse with trained operators and synced ERP.'
      ),
      metrics: [
        { label: i18n('Erreurs de stock', 'Stock errors'), value: i18n('-60 %', '-60%') },
        { label: i18n('Temps de préparation', 'Picking time'), value: i18n('-30 %', '-30%') },
        { label: i18n('Fiabilité des stocks', 'Stock reliability'), value: i18n('+35 %', '+35%') },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n('Du flux papier au flux digital', 'From paper flow to digital flow'),
      subtitle: i18n(
        'Nous commençons par un entrepôt pilote pour prouver la valeur avant d’étendre.',
        'We start with a pilot warehouse to prove value before scaling.'
      ),
      steps: [
        {
          title: i18n('Cartographie des flux', 'Flow mapping'),
          description: i18n(
            'Observation sur le terrain et identification des flux à digitaliser en priorité.',
            'On-site observation and identification of priority flows to digitize.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('MVP WMS + connecteur ERP', 'WMS MVP + ERP connector'),
          description: i18n(
            'Développement de l’interface opérateur et de la synchronisation avec l’ERP.',
            'Development of operator interface and sync with ERP.'
          ),
          duration: i18n('Semaines 2-6', 'Weeks 2-6'),
        },
        {
          title: i18n('Déploiement pilote', 'Pilot rollout'),
          description: i18n(
            'Test avec quelques opérateurs, ajustements et documentation.',
            'Test with a few operators, adjustments, and documentation.'
          ),
          duration: i18n('Semaines 7-8', 'Weeks 7-8'),
        },
      ],
    },
    cta: {
      title: i18n('Digitalisez votre entrepôt', 'Digitize your warehouse'),
      highlightedTitle: i18n('sans changer d’ERP', 'without changing ERP'),
      description: i18n(
        'On vous montre comment un WMS MVP peut s’intégrer à votre ERP en 20 minutes.',
        'We’ll show you how a WMS MVP can integrate with your ERP in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },
  {
    slug: 'suivi-temps-reel-des-tournees',
    sectorSlug: 'logistics',
    image:
      'https://ik.imagekit.io/lindahestia/articles/04-suivi-temps-reel-tournees.webp?tr=f-webp',
    imageAlt: i18n(
      'Suivi temps réel des tournées de livraison',
      'Real-time delivery route tracking'
    ),
    northStarQuery: {
      sector: 'logistics',
      prompt: i18n(
        'Les tournées sont mal suivies et les litiges de livraison sont fréquents',
        'Routes are poorly tracked and delivery disputes are frequent'
      ),
    },
    seo: {
      title: i18n(
        'MVP Suivi temps réel tournées | Averiane',
        'Real-time route tracking MVP | Averiane'
      ),
      description: i18n(
        'Un MVP de suivi temps réel des tournées pour réduire les litiges et améliorer la satisfaction client.',
        'A real-time route tracking MVP to reduce disputes and improve customer satisfaction.'
      ),
      keywords: i18nArr(
        ['suivi temps réel', 'tournées', 'livraison', 'MVP logistique', 'supply chain'],
        ['real-time tracking', 'routes', 'delivery', 'logistics MVP', 'supply chain']
      ),
    },
    hero: {
      eyebrow: i18n('Logistique & Supply Chain', 'Logistics & Supply Chain'),
      title: i18n('Suivi temps réel', 'Real-time route'),
      highlightedTitle: i18n('des tournées', 'tracking'),
      description: i18n(
        'Localisez vos livreurs, anticipez les retards et prouvez les livraisons avec preuves horodatées pour réduire les litiges.',
        'Locate your drivers, anticipate delays, and prove deliveries with timestamped evidence to reduce disputes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n('Où en est la livraison ?', 'Where is the delivery?'),
      subtitle: i18n(
        'Les clients appellent, le service client ne sait pas répondre, et les litiges s’accumulent faute de preuves.',
        'Customers call, customer service cannot answer, and disputes pile up for lack of proof.'
      ),
      pains: [
        {
          title: i18n('Aucune visibilité en cours de route', 'No visibility during route'),
          description: i18n(
            'Une fois le camion parti, personne ne sait où il en est ni quand il arrivera.',
            'Once the truck leaves, no one knows where it is or when it will arrive.'
          ),
        },
        {
          title: i18n('Litiges coûteux', 'Costly disputes'),
          description: i18n(
            '“Vous n’êtes pas passé”, “j’étais là pourtant” : sans preuve, ces litiges coûtent du temps et de l’argent.',
            '“You did not come,” “I was there”: without proof, these disputes cost time and money.'
          ),
        },
        {
          title: i18n('Planification au petit bonheur', 'Haphazard planning'),
          description: i18n(
            'Les tournées sont construites à la main, sans optimisation ni prise en compte du trafic.',
            'Routes are built manually, with no optimization or traffic consideration.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Une trace numérique de chaque tournée', 'A digital trace of every route'),
      description: i18n(
        'Application mobile pour les livreurs, carte temps réel pour le superviseur et preuves de livraison avec photo/signature.',
        'Mobile app for drivers, real-time map for supervisors, and delivery proof with photo/signature.'
      ),
      features: [
        {
          title: i18n('Géolocalisation temps réel', 'Real-time geolocation'),
          description: i18n(
            'Position du livreur, ETA par client et alertes de retard automatiques.',
            'Driver position, ETA per customer, and automatic delay alerts.'
          ),
        },
        {
          title: i18n('Preuve de livraison', 'Delivery proof'),
          description: i18n(
            'Photo, signature et horodatage pour clore les litiges dès qu’ils apparaissent.',
            'Photo, signature, and timestamp to close disputes as soon as they arise.'
          ),
        },
        {
          title: i18n('Supervision centralisée', 'Centralized supervision'),
          description: i18n(
            'Un écran unique pour suivre l’ensemble des tournées et réagir aux imprévus.',
            'One screen to follow all routes and react to incidents.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 6 semaines', 'What you gain in 6 weeks'),
      subtitle: i18n(
        'Une visibilité complète et une réduction nette des litiges dès la première vague de livraisons.',
        'Full visibility and a clear reduction in disputes from the first delivery wave.'
      ),
      metrics: [
        { label: i18n('Litiges livraison', 'Delivery disputes'), value: i18n('-50 %', '-50%') },
        {
          label: i18n('Appels service client', 'Customer service calls'),
          value: i18n('-40 %', '-40%'),
        },
        {
          label: i18n('Satisfaction client', 'Customer satisfaction'),
          value: i18n('+20 %', '+20%'),
        },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n('De la tournée papier au suivi digital', 'From paper route to digital tracking'),
      subtitle: i18n(
        'Nous accompagnons quelques livreurs sur une zone pilote pour valider la chaîne.',
        'We support a few drivers on a pilot zone to validate the chain.'
      ),
      steps: [
        {
          title: i18n('Cadrage des tournées', 'Route framing'),
          description: i18n(
            'Sélection d’un périmètre pilote et définition des preuves de livraison attendues.',
            'Selection of a pilot scope and definition of expected delivery proofs.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('App livreur + supervision', 'Driver app + supervision'),
          description: i18n(
            'Développement de l’application mobile et du tableau de bord superviseur.',
            'Development of mobile app and supervisor dashboard.'
          ),
          duration: i18n('Semaines 2-4', 'Weeks 2-4'),
        },
        {
          title: i18n('Test terrain', 'Field test'),
          description: i18n(
            'Tournées pilotes, retours livreurs/clients et ajustements.',
            'Pilot routes, driver/customer feedback, and adjustments.'
          ),
          duration: i18n('Semaines 5-6', 'Weeks 5-6'),
        },
      ],
    },
    cta: {
      title: i18n('Suivez vos livraisons', 'Track your deliveries'),
      highlightedTitle: i18n('en temps réel', 'in real time'),
      description: i18n(
        'On vous montre comment réduire les litiges et rassurer vos clients en 20 minutes.',
        'We’ll show you how to reduce disputes and reassure customers in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },
  {
    slug: 'optimisation-des-tournees',
    sectorSlug: 'logistics',
    image: 'https://placehold.co/1200x800/1d6fb8/ffffff?text=Tourn%C3%A9es',
    imageAlt: i18n('Optimisation des tournées de livraison', 'Delivery route optimization'),
    northStarQuery: {
      sector: 'supply-chain',
      prompt: i18n(
        'Les tournées sont sous-optimisées et coûtent du temps',
        'Routes are under-optimized and cost time'
      ),
    },
    seo: {
      title: i18n('MVP Optimisation des tournées | Averiane', 'Route optimization MVP | Averiane'),
      description: i18n(
        'Un MVP d’optimisation de tournées pour réduire les kilomètres, le temps de conduite et les coûts de livraison.',
        'A route optimization MVP to reduce miles, driving time, and delivery costs.'
      ),
      keywords: i18nArr(
        ['optimisation tournées', 'logistique', 'supply chain', 'MVP logistique', 'VRP'],
        ['route optimization', 'logistics', 'supply chain', 'logistics MVP', 'VRP']
      ),
    },
    hero: {
      eyebrow: i18n('Logistique & Supply Chain', 'Logistics & Supply Chain'),
      title: i18n('Optimisation', 'Route'),
      highlightedTitle: i18n('des tournées', 'optimization'),
      description: i18n(
        'Générez automatiquement des tournées optimisées en tenant compte des contraintes, du trafic et des fenêtres de livraison.',
        'Automatically generate optimized routes considering constraints, traffic, and delivery windows.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n('Des tournées construites à la main', 'Routes built by hand'),
      subtitle: i18n(
        'Kilomètres inutiles, livreurs surchargés, clients mécontents : la planification manuelle a atteint ses limites.',
        'Unnecessary miles, overloaded drivers, unhappy customers: manual planning has reached its limits.'
      ),
      pains: [
        {
          title: i18n('Routes sous-optimales', 'Suboptimal routes'),
          description: i18n(
            'Le livreur fait du zigzag tandis qu’un autre passe à côté du même client.',
            'One driver zigzags while another passes right by the same customer.'
          ),
        },
        {
          title: i18n('Contraintes ignorées', 'Ignored constraints'),
          description: i18n(
            'Créneaux clients, temps de chargement, capacité des véhicules : tout est dans la tête du planificateur.',
            'Customer time slots, loading times, vehicle capacity: all in the planner’s head.'
          ),
        },
        {
          title: i18n('Pas de simulation', 'No simulation'),
          description: i18n(
            'Impossible de tester l’impact d’une nouvelle tournée ou d’un nouvel client avant le lendemain.',
            'Impossible to test the impact of a new route or new customer before the next day.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un optimiseur de tournées MVP', 'A route optimizer MVP'),
      description: i18n(
        'Import des commandes, calcul des routes optimisées, envoi aux livreurs et suivi des écarts.',
        'Import orders, calculate optimized routes, send to drivers, and track deviations.'
      ),
      features: [
        {
          title: i18n('Optimisation automatique', 'Automatic optimization'),
          description: i18n(
            'Algorithme qui minimise la distance et le temps tout en respectant vos contraintes.',
            'Algorithm that minimizes distance and time while respecting your constraints.'
          ),
        },
        {
          title: i18n('Import multi-sources', 'Multi-source import'),
          description: i18n(
            'CSV, ERP ou API : les commandes du jour alimentent l’optimiseur en quelques clics.',
            'CSV, ERP, or API: today’s orders feed the optimizer in a few clicks.'
          ),
        },
        {
          title: i18n('Simulation et comparaison', 'Simulation and comparison'),
          description: i18n(
            'Comparez votre planification actuelle avec la version optimisée avant de l’envoyer.',
            'Compare your current planning with the optimized version before sending it.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 8 semaines', 'What you gain in 8 weeks'),
      subtitle: i18n(
        'Des tournées pilotes optimisées avec un calcul précis du gain kilométrique et horaire.',
        'Optimized pilot routes with precise mileage and time savings.'
      ),
      metrics: [
        { label: i18n('Distance parcourue', 'Distance traveled'), value: i18n('-20 %', '-20%') },
        { label: i18n('Temps de conduite', 'Driving time'), value: i18n('-15 %', '-15%') },
        {
          label: i18n('Livraisons par tournée', 'Deliveries per route'),
          value: i18n('+18 %', '+18%'),
        },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n(
        'De la planification manuelle à l’optimisation',
        'From manual planning to optimization'
      ),
      subtitle: i18n(
        'Nous modélisons vos contraintes puis nous optimisons un sous-ensemble de tournées réelles.',
        'We model your constraints, then optimize a subset of real routes.'
      ),
      steps: [
        {
          title: i18n('Modélisation des contraintes', 'Constraint modeling'),
          description: i18n(
            'Véhicules, créneaux, zones, compétences livreurs et règles métier.',
            'Vehicles, time slots, zones, driver skills, and business rules.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('MVP optimiseur', 'Optimizer MVP'),
          description: i18n(
            'Développement du moteur d’optimisation et de l’interface de planification.',
            'Optimization engine and planning interface development.'
          ),
          duration: i18n('Semaines 2-6', 'Weeks 2-6'),
        },
        {
          title: i18n('Test sur tournées réelles', 'Test on real routes'),
          description: i18n(
            'Comparaison avant/après, ajustements et documentation pour le déploiement.',
            'Before/after comparison, adjustments, and deployment documentation.'
          ),
          duration: i18n('Semaines 7-8', 'Weeks 7-8'),
        },
      ],
    },
    cta: {
      title: i18n('Optimisez vos tournées', 'Optimize your routes'),
      highlightedTitle: i18n('sans rupture', 'seamlessly'),
      description: i18n(
        'On vous montre le potentiel de gain sur vos tournées réelles en 20 minutes.',
        'We’ll show you the potential savings on your real routes in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },

  // ─────────────────────────────────────────────────────────────
  // PHARMACEUTIQUE & SANTÉ
  // ─────────────────────────────────────────────────────────────
  {
    slug: 'tracabilite-numerique-lots',
    sectorSlug: 'pharmaceutics',
    image: 'https://placehold.co/1200x800/1d6fb8/ffffff?text=Lots',
    imageAlt: i18n(
      'Traçabilité numérique des lots pharmaceutiques',
      'Digital pharmaceutical batch traceability'
    ),
    northStarQuery: {
      sector: 'pharmaceutics',
      prompt: i18n(
        'La traçabilité des lots est fragmentée et les audits sont stressants',
        'Batch traceability is fragmented and audits are stressful'
      ),
    },
    seo: {
      title: i18n(
        'MVP Traçabilité numérique des lots | Averiane',
        'Digital batch traceability MVP | Averiane'
      ),
      description: i18n(
        'Un MVP de traçabilité numérique des lots pour l’industrie pharmaceutique : historique, conformité et audits simplifiés.',
        'A digital batch traceability MVP for pharma: history, compliance, and simplified audits.'
      ),
      keywords: i18nArr(
        ['traçabilité lots', 'pharmaceutique', 'GMP', 'MVP pharma', 'audit'],
        ['batch traceability', 'pharmaceutical', 'GMP', 'pharma MVP', 'audit']
      ),
    },
    hero: {
      eyebrow: i18n('Pharmaceutique & Santé', 'Pharmaceutical & Health'),
      title: i18n('Traçabilité numérique', 'Digital batch'),
      highlightedTitle: i18n('des lots', 'traceability'),
      description: i18n(
        'Retracez l’historique complet d’un lot en quelques secondes : matières, opérateurs, équipements et contrôles, tous centralisés.',
        'Trace the complete history of a batch in seconds: materials, operators, equipment, and controls, all centralized.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n(
        'Des audits qui mobilisent toute l’équipe',
        'Audits that mobilize the whole team'
      ),
      subtitle: i18n(
        'Les informations sur un lot sont éparpillées dans des dossiers, des fichiers et des systèmes hétérogènes.',
        'Information about a batch is scattered across folders, files, and heterogeneous systems.'
      ),
      pains: [
        {
          title: i18n('Recherche manuelle', 'Manual search'),
          description: i18n(
            'Pour répondre à un auditeur, il faut ouvrir plusieurs applications et dossiers papier.',
            'To answer an auditor, you must open several applications and paper folders.'
          ),
        },
        {
          title: i18n('Risque d’erreur humaine', 'Human error risk'),
          description: i18n(
            'Un mauvais lot, un opérateur mal saisi, une date incorrecte : chaque détail peut bloquer un audit.',
            'Wrong batch, mis-entered operator, incorrect date: every detail can block an audit.'
          ),
        },
        {
          title: i18n('Traçabilité incomplète', 'Incomplete traceability'),
          description: i18n(
            'Impossible de remonter rapidement aux matières premières ou aux sous-traitants impliqués.',
            'Impossible to quickly trace back to raw materials or subcontractors involved.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un carnet de lot numérique', 'A digital batch record'),
      description: i18n(
        'Le MVP centralise les données du lot et offre une recherche instantanée par numéro de lot, matière ou opérateur.',
        'The MVP centralizes batch data and offers instant search by batch number, material, or operator.'
      ),
      features: [
        {
          title: i18n('Fiche lot unifiée', 'Unified batch record'),
          description: i18n(
            'Matières, opérations, contrôles et opérateurs sur une seule page consultable.',
            'Materials, operations, controls, and operators on one viewable page.'
          ),
        },
        {
          title: i18n('Recherche et filtre', 'Search and filter'),
          description: i18n(
            'Trouvez un lot, une matière ou un sous-traitant en quelques secondes.',
            'Find a batch, material, or subcontractor in seconds.'
          ),
        },
        {
          title: i18n('Historique et conformité', 'History and compliance'),
          description: i18n(
            'Historique des modifications, signatures électroniques et exports audit-ready.',
            'Change history, electronic signatures, and audit-ready exports.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 8 semaines', 'What you gain in 8 weeks'),
      subtitle: i18n(
        'Un audit de lot réduit à quelques clics et une traçabilité vérifiable en temps réel.',
        'A batch audit reduced to a few clicks and real-time verifiable traceability.'
      ),
      metrics: [
        {
          label: i18n('Temps de préparation audit', 'Audit prep time'),
          value: i18n('-75 %', '-75%'),
        },
        { label: i18n('Recherches lot', 'Batch searches'), value: i18n('< 10 s', '< 10s') },
        { label: i18n('Erreurs documentaires', 'Document errors'), value: i18n('-60 %', '-60%') },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n(
        'Des sources dispersées à la fiche lot',
        'From scattered sources to batch record'
      ),
      subtitle: i18n(
        'Nous identifions les sources de vérité puis nous créons le portail de traçabilité.',
        'We identify source systems, then create the traceability portal.'
      ),
      steps: [
        {
          title: i18n('Cartographie des données', 'Data mapping'),
          description: i18n(
            'Identification des systèmes, fichiers et règles de gestion par lot.',
            'Identification of systems, files, and batch business rules.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('Portail de traçabilité', 'Traceability portal'),
          description: i18n(
            'Développement de la fiche lot, recherche, historique et exports.',
            'Development of batch record, search, history, and exports.'
          ),
          duration: i18n('Semaines 2-6', 'Weeks 2-6'),
        },
        {
          title: i18n('Audit test', 'Audit test'),
          description: i18n(
            'Simulation d’audit avec vos QA, ajustements et documentation.',
            'Audit simulation with your QA, adjustments, and documentation.'
          ),
          duration: i18n('Semaines 7-8', 'Weeks 7-8'),
        },
      ],
    },
    cta: {
      title: i18n('Retracez vos lots', 'Trace your batches'),
      highlightedTitle: i18n('en quelques secondes', 'in seconds'),
      description: i18n(
        'On vous montre comment un MVP de traçabilité simplifie vos audits en 20 minutes.',
        'We’ll show you how a traceability MVP simplifies your audits in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },
  {
    slug: 'gestion-des-ecarts-et-capa',
    sectorSlug: 'pharmaceutics',
    image: 'https://placehold.co/1200x800/1d6fb8/ffffff?text=%C3%89carts',
    imageAlt: i18n('Gestion des écarts et actions correctives', 'Deviation and CAPA management'),
    northStarQuery: {
      sector: 'pharmaceutics',
      prompt: i18n(
        'La gestion des écarts est manuelle et les preuves de conformité sont dispersées',
        'Deviation management is manual and compliance evidence is scattered'
      ),
    },
    seo: {
      title: i18n(
        'MVP Gestion des écarts et CAPA | Averiane',
        'Deviation and CAPA management MVP | Averiane'
      ),
      description: i18n(
        'Un MVP de gestion des écarts et CAPA pour centraliser la détection, l’analyse et le suivi des actions correctives.',
        'A deviation and CAPA management MVP to centralize detection, analysis, and corrective action tracking.'
      ),
      keywords: i18nArr(
        ['écarts', 'CAPA', 'pharmaceutique', 'GMP', 'MVP pharma'],
        ['deviation', 'CAPA', 'pharmaceutical', 'GMP', 'pharma MVP']
      ),
    },
    hero: {
      eyebrow: i18n('Pharmaceutique & Santé', 'Pharmaceutical & Health'),
      title: i18n('Gestion des écarts', 'Deviation &'),
      highlightedTitle: i18n('et CAPA', 'CAPA management'),
      description: i18n(
        'Centralisez la déclaration, l’analyse et le suivi des CAPA dans un workflow structuré et traçable pour vos audits.',
        'Centralize reporting, analysis, and CAPA tracking in a structured, traceable workflow for your audits.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n('Les écarts se perdent dans les emails', 'Deviations get lost in emails'),
      subtitle: i18n(
        'Déclaration informelle, analyse dans des fichiers Excel, actions non suivies : la conformité est impossible à prouver.',
        'Informal reporting, analysis in Excel files, untracked actions: compliance is impossible to prove.'
      ),
      pains: [
        {
          title: i18n('Déclarations tardives', 'Late reporting'),
          description: i18n(
            'Sans canal simple, les écarts sont signalés avec retard, quand les preuves ont disparu.',
            'Without a simple channel, deviations are reported late, when evidence is gone.'
          ),
        },
        {
          title: i18n('Actions non suivies', 'Untracked actions'),
          description: i18n(
            'Les CAPA sont listées mais personne ne sait qui en est responsable ni si elles sont terminées.',
            'CAPAs are listed but no one knows who owns them or whether they are done.'
          ),
        },
        {
          title: i18n('Peu de visibilité management', 'Poor management visibility'),
          description: i18n(
            'Pas de tableau de bord pour prioriser les écarts récurrents et mesurer l’amélioration.',
            'No dashboard to prioritize recurring deviations and measure improvement.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un workflow écart → CAPA → preuve', 'A deviation → CAPA → proof workflow'),
      description: i18n(
        'Le MVP structure chaque écart : déclaration, analyse cause racine, plan d’action et validation de l’efficacité.',
        'The MVP structures every deviation: reporting, root cause analysis, action plan, and effectiveness validation.'
      ),
      features: [
        {
          title: i18n('Déclaration simplifiée', 'Simplified reporting'),
          description: i18n(
            'Formulaire court, pièces jointes et notifications automatiques aux responsables.',
            'Short form, attachments, and automatic notifications to owners.'
          ),
        },
        {
          title: i18n('Workflow structuré', 'Structured workflow'),
          description: i18n(
            'Étapes obligatoires : analyse, CAPA, plan d’action et vérification d’efficacité.',
            'Mandatory steps: analysis, CAPA, action plan, and effectiveness verification.'
          ),
        },
        {
          title: i18n('Dashboard et alertes', 'Dashboard and alerts'),
          description: i18n(
            'Vue des écarts en cours, CAPA en retard et tendances par catégorie.',
            'View of open deviations, overdue CAPAs, and trends by category.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 8 semaines', 'What you gain in 8 weeks'),
      subtitle: i18n(
        'Un workflow de CAPA opérationnel et des écarts traçables dès le premier mois.',
        'An operational CAPA workflow and traceable deviations from the first month.'
      ),
      metrics: [
        { label: i18n('Temps de déclaration', 'Reporting time'), value: i18n('-60 %', '-60%') },
        { label: i18n('CAPA en retard', 'Overdue CAPAs'), value: i18n('-45 %', '-45%') },
        { label: i18n('Préparation audit', 'Audit readiness'), value: i18n('+3×', '3×') },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n(
        'Du process papier au workflow digital',
        'From paper process to digital workflow'
      ),
      subtitle: i18n(
        'Nous adaptons le workflow à votre SOP actuelle et nous le testons sur des écarts réels.',
        'We adapt the workflow to your current SOP and test it on real deviations.'
      ),
      steps: [
        {
          title: i18n('Atelier SOP', 'SOP workshop'),
          description: i18n(
            'Cartographie du process existant et définition des rôles/étapes obligatoires.',
            'Mapping of existing process and definition of roles/mandatory steps.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('MVP workflow', 'Workflow MVP'),
          description: i18n(
            'Développement du formulaire, des workflows et du dashboard.',
            'Development of form, workflows, and dashboard.'
          ),
          duration: i18n('Semaines 2-6', 'Weeks 2-6'),
        },
        {
          title: i18n('Test sur écarts pilotes', 'Test on pilot deviations'),
          description: i18n(
            'Déclaration et traitement d’écarts réels, ajustements et transfert QA.',
            'Reporting and processing of real deviations, adjustments, and QA handover.'
          ),
          duration: i18n('Semaines 7-8', 'Weeks 7-8'),
        },
      ],
    },
    cta: {
      title: i18n('Structurez vos écarts', 'Structure your deviations'),
      highlightedTitle: i18n('et vos CAPA', 'and CAPAs'),
      description: i18n(
        'On vous montre comment un MVP de gestion des écarts facilite vos audits en 20 minutes.',
        'We’ll show you how a deviation management MVP eases your audits in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },
  {
    slug: 'cockpit-qms',
    sectorSlug: 'pharmaceutics',
    image: 'https://placehold.co/1200x800/1d6fb8/ffffff?text=QMS',
    imageAlt: i18n('Cockpit QMS pharmaceutique', 'Pharmaceutical QMS cockpit'),
    northStarQuery: {
      sector: 'pharma',
      prompt: i18n(
        'La gestion des écarts et CAPA est fragmentée',
        'Deviation and CAPA management is fragmented'
      ),
    },
    seo: {
      title: i18n('MVP Cockpit QMS | Averiane', 'QMS cockpit MVP | Averiane'),
      description: i18n(
        'Un cockpit QMS pour piloter écarts, CAPA, audits et indicateurs qualité dans une vue unique et actionnable.',
        'A QMS cockpit to steer deviations, CAPAs, audits, and quality indicators in one actionable view.'
      ),
      keywords: i18nArr(
        ['QMS', 'cockpit qualité', 'pharmaceutique', 'GMP', 'MVP pharma'],
        ['QMS', 'quality cockpit', 'pharmaceutical', 'GMP', 'pharma MVP']
      ),
    },
    hero: {
      eyebrow: i18n('Pharmaceutique & Santé', 'Pharmaceutical & Health'),
      title: i18n('Cockpit', 'QMS'),
      highlightedTitle: i18n('QMS', 'cockpit'),
      description: i18n(
        'Pilotez votre système qualité dans un cockpit unique : écarts, CAPA, audits et indicateurs clairs, mis à jour en temps réel.',
        'Steer your quality system in one cockpit: deviations, CAPAs, audits, and clear indicators updated in real time.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
    problem: {
      eyebrow: i18n('La friction', 'The friction'),
      title: i18n('Le QMS vit dans des silos', 'The QMS lives in silos'),
      subtitle: i18n(
        'Écarts dans un outil, CAPA dans un autre, audits sur Excel : impossible d’avoir une vision synthétique de la qualité.',
        'Deviations in one tool, CAPAs in another, audits in Excel: impossible to get a synthetic quality view.'
      ),
      pains: [
        {
          title: i18n('Données dispersées', 'Scattered data'),
          description: i18n(
            'Les responsables qualité passent leur temps à agréger des sources sans jamais avoir la bonne vue.',
            'Quality managers spend their time aggregating sources without ever getting the right view.'
          ),
        },
        {
          title: i18n('Réactivité faible', 'Low reactivity'),
          description: i18n(
            'Les dérives ne sont repérées que lors des revues trimestrielles, souvent trop tard.',
            'Drifts are only spotted during quarterly reviews, often too late.'
          ),
        },
        {
          title: i18n('Reporting chronophage', 'Time-consuming reporting'),
          description: i18n(
            'Chaque rapport de management qualité exige des heures de compilation manuelle.',
            'Each quality management report requires hours of manual compilation.'
          ),
        },
      ],
    },
    concept: {
      eyebrow: i18n('La solution', 'The solution'),
      title: i18n('Un cockpit qualité unifié', 'A unified quality cockpit'),
      description: i18n(
        'Le MVP agrège les données critiques du QMS et propose des indicateurs, des alertes et des exports pour le management.',
        'The MVP aggregates critical QMS data and offers indicators, alerts, and exports for management.'
      ),
      features: [
        {
          title: i18n('Indicateurs clés en temps réel', 'Real-time key indicators'),
          description: i18n(
            'Écarts ouverts, CAPA à échéance, audits à venir, taux de clôture : tout en un écran.',
            'Open deviations, due CAPAs, upcoming audits, closure rate: all on one screen.'
          ),
        },
        {
          title: i18n('Alertes et escalade', 'Alerts and escalation'),
          description: i18n(
            'Notifications automatiques quand un indicateur dépasse un seuil ou qu’une CAPA est en retard.',
            'Automatic notifications when an indicator crosses a threshold or a CAPA is overdue.'
          ),
        },
        {
          title: i18n('Exports management', 'Management exports'),
          description: i18n(
            'Rapports mensuels/trimestriels générés automatiquement pour les revues de direction.',
            'Monthly/quarterly reports automatically generated for management reviews.'
          ),
        },
      ],
    },
    impact: {
      eyebrow: i18n('Impact mesurable', 'Measurable impact'),
      title: i18n('Ce que vous gagnez en 6 semaines', 'What you gain in 6 weeks'),
      subtitle: i18n(
        'Une vision unifiée du QMS et des décisions de management plus rapides.',
        'A unified QMS view and faster management decisions.'
      ),
      metrics: [
        { label: i18n('Temps de reporting', 'Reporting time'), value: i18n('-70 %', '-70%') },
        { label: i18n('Réactivité aux écarts', 'Deviation reactivity'), value: i18n('+2×', '2×') },
        {
          label: i18n('Visibilité management', 'Management visibility'),
          value: i18n('Temps réel', 'Real-time'),
        },
      ],
    },
    delivery: {
      eyebrow: i18n('Comment on le livre', 'How we deliver it'),
      title: i18n('Des silos au cockpit', 'From silos to cockpit'),
      subtitle: i18n(
        'Nous connectons vos sources QMS et concevons les indicateurs avec vos responsables qualité.',
        'We connect your QMS sources and design indicators with your quality managers.'
      ),
      steps: [
        {
          title: i18n('Atelier indicateurs', 'Indicator workshop'),
          description: i18n(
            'Définition des KPIs critiques et identification des sources de données.',
            'Definition of critical KPIs and identification of data sources.'
          ),
          duration: i18n('Semaine 1', 'Week 1'),
        },
        {
          title: i18n('MVP cockpit', 'Cockpit MVP'),
          description: i18n(
            'Développement du dashboard, connecteurs sources et système d’alertes.',
            'Dashboard development, source connectors, and alert system.'
          ),
          duration: i18n('Semaines 2-4', 'Weeks 2-4'),
        },
        {
          title: i18n('Revues et transfert', 'Reviews and handover'),
          description: i18n(
            'Tests en revue de direction, ajustements et documentation.',
            'Tests in management review, adjustments, and documentation.'
          ),
          duration: i18n('Semaines 5-6', 'Weeks 5-6'),
        },
      ],
    },
    cta: {
      title: i18n('Pilotez votre QMS', 'Steer your QMS'),
      highlightedTitle: i18n('sans silos', 'without silos'),
      description: i18n(
        'On vous montre comment un cockpit QMS MVP clarifie la qualité en 20 minutes.',
        'We’ll show you how a QMS cockpit MVP clarifies quality in 20 minutes.'
      ),
      primaryCta: i18n('Prendre rendez-vous', 'Book a meeting'),
      secondaryCta: i18n('Simuler l’impact', 'Simulate impact'),
    },
  },
];

export function getMvpPage(sectorSlug: string, mvpSlug: string): MvpPageEntry | undefined {
  return mvpPageEntries.find((entry) => entry.sectorSlug === sectorSlug && entry.slug === mvpSlug);
}

export function getMvpBySlug(mvpSlug: string): MvpPageEntry | undefined {
  return mvpPageEntries.find((entry) => entry.slug === mvpSlug);
}

export function getMvpsBySector(sectorSlug: string): MvpPageEntry[] {
  return mvpPageEntries.filter((entry) => entry.sectorSlug === sectorSlug);
}

export function getAllMvpParams(): { lang: Locale; sector: string; mvpSlug: string }[] {
  const locales: Locale[] = ['fr', 'en'];
  return mvpPageEntries.flatMap((entry) =>
    locales.map((lang) => ({ lang, sector: entry.sectorSlug, mvpSlug: entry.slug }))
  );
}

export function buildMvpPath(entry: MvpPageEntry): string {
  return `/expertise/${entry.sectorSlug}/mvp/${entry.slug}`;
}

type NorthStarEntry = {
  northStarQuery: {
    sector: string;
    prompt: Record<string, string>;
  };
};

export function buildNorthStarHref(entry: NorthStarEntry, lang: Locale): string {
  const prompt = encodeURIComponent(entry.northStarQuery.prompt[lang] ?? '');
  const sector = encodeURIComponent(entry.northStarQuery.sector);
  return `/${lang}/north-star-dashboard?sector=${sector}&prompt=${prompt}`;
}

export { slugify };
